import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTour } from "../contexts/PageTourContext";
import {
  getGuideDescKey,
  getGuideTitleKey,
  PAGE_GUIDE_CATEGORIES,
} from "../data/pageGuides";
import { findFieldTarget, findTourTarget } from "../utils/pageTourTarget";
import "./css/PageTour.css";

const CARD_OFFSET = 16;
const HIGHLIGHT_PAD = 6;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function PageTourOverlay() {
  const { t, i18n } = useTranslation("mobile");
  const {
    active,
    guide,
    detail,
    detailsOpen,
    hasDetails,
    pageIndex,
    totalPages,
    detailIndex,
    totalDetails,
    isFirstPage,
    isLastPage,
    isLastDetail,
    stopTour,
    nextPage,
    prevPage,
    openDetails,
    nextDetail,
    prevDetail,
  } = usePageTour();
  const [targetRect, setTargetRect] = useState(null);
  const [cardSize, setCardSize] = useState({ width: 400, height: 280 });
  const isRtl = String(i18n.language || "en").toLowerCase().startsWith("ar");

  const categoryLabel = useMemo(() => {
    const category = PAGE_GUIDE_CATEGORIES.find((item) => item.id === guide?.category);
    return t(category?.titleKey || "how_it_works", { defaultValue: "Overview" });
  }, [guide, t]);

  const cardTitle = guide?.titleKey
    ? t(guide.titleKey)
    : guide
      ? t(getGuideTitleKey(guide.key))
      : "";
  const cardBody = guide?.bodyKey
    ? t(guide.bodyKey)
    : guide
      ? t(getGuideDescKey(guide.key))
      : "";

  useLayoutEffect(() => {
    if (!active || !guide) {
      setTargetRect(null);
      return undefined;
    }

    let stopped = false;
    const measure = () => {
      if (stopped) return;
      let target = null;
      if (guide.fieldId) {
        target = findFieldTarget(guide.key, guide.fieldId);
      } else {
        window.dispatchEvent(
          new CustomEvent("page-tour:reveal", { detail: { key: guide.key } })
        );
        target = findTourTarget(guide.key);
      }
      if (!target) {
        setTargetRect((prev) => (prev ? null : prev));
        return;
      }
      const inSidebar = Boolean(target.closest(".sidebar"));
      target.scrollIntoView({
        block: inSidebar ? "nearest" : "center",
        inline: "nearest",
      });
      const rect = target.getBoundingClientRect();
      const nextRect = {
        top: rect.top - HIGHLIGHT_PAD,
        left: rect.left - HIGHLIGHT_PAD,
        width: rect.width + HIGHLIGHT_PAD * 2,
        height: rect.height + HIGHLIGHT_PAD * 2,
      };
      setTargetRect((prev) => {
        if (
          prev &&
          Math.abs(prev.top - nextRect.top) < 1 &&
          Math.abs(prev.left - nextRect.left) < 1 &&
          Math.abs(prev.width - nextRect.width) < 1 &&
          Math.abs(prev.height - nextRect.height) < 1
        ) {
          return prev;
        }
        return nextRect;
      });
    };

    measure();
    const interval = window.setInterval(measure, 200);
    const stopPolling = window.setTimeout(() => window.clearInterval(interval), 8000);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      stopped = true;
      window.clearInterval(interval);
      window.clearTimeout(stopPolling);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, guide, detail]);

  useEffect(() => {
    if (!active) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        stopTour();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        if (detailsOpen) nextDetail();
        else nextPage();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (detailsOpen) prevDetail();
        else prevPage();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, detailsOpen, nextDetail, nextPage, prevDetail, prevPage, stopTour]);

  if (!active || !guide) return null;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(400, viewportWidth - 32);

  let cardTop = 88;
  let cardLeft = isRtl ? viewportWidth - cardWidth - 24 : 280;

  if (targetRect) {
    const spaceRight = viewportWidth - (targetRect.left + targetRect.width) - CARD_OFFSET;
    const spaceLeft = targetRect.left - CARD_OFFSET;
    const targetArea = targetRect.width * targetRect.height;
    const viewportArea = viewportWidth * viewportHeight;
    const placeBelow = () => {
      cardTop = targetRect.top + targetRect.height + CARD_OFFSET;
      cardLeft = clamp(
        isRtl ? targetRect.left + targetRect.width - cardWidth : targetRect.left,
        16,
        viewportWidth - cardWidth - 16
      );
    };

    if (targetArea > viewportArea * 0.28) {
      cardTop = Math.max(24, Math.round((viewportHeight - cardSize.height) / 2));
      cardLeft = isRtl ? 24 : viewportWidth - cardWidth - 24;
    } else if (isRtl) {
      if (spaceLeft >= cardWidth) {
        cardTop = targetRect.top;
        cardLeft = targetRect.left - CARD_OFFSET - cardWidth;
      } else if (spaceRight >= cardWidth) {
        cardTop = targetRect.top;
        cardLeft = targetRect.left + targetRect.width + CARD_OFFSET;
      } else {
        placeBelow();
      }
    } else if (spaceRight >= cardWidth) {
      cardTop = targetRect.top;
      cardLeft = targetRect.left + targetRect.width + CARD_OFFSET;
    } else if (spaceLeft >= cardWidth) {
      cardTop = targetRect.top;
      cardLeft = targetRect.left - CARD_OFFSET - cardWidth;
    } else {
      placeBelow();
    }
  }

  cardLeft = clamp(cardLeft, 16, viewportWidth - cardWidth - 16);
  cardTop = clamp(cardTop, 16, Math.max(16, viewportHeight - cardSize.height - 16));

  const title = detailsOpen
    ? t(detail.titleKey)
    : t(getGuideTitleKey(guide.key));
  const body = detailsOpen
    ? t(detail.descKey)
    : t(getGuideDescKey(guide.key));
  const stepLabel = detailsOpen
    ? t("how_it_works_detail_stop", {
        current: detailIndex + 1,
        total: totalDetails,
      })
    : t("how_it_works_stop", {
        current: pageIndex + 1,
        total: totalPages,
      });
  const nextLabel = detailsOpen
    ? isLastDetail
      ? t("how_it_works_got_it", { defaultValue: "Got it" })
      : t("how_it_works_next")
    : isLastPage
      ? t("how_it_works_done")
      : t("how_it_works_next");

  return (
    <div
      className={`page-tour${targetRect ? " page-tour--spotlight" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="page-tour-title"
      onClick={targetRect ? undefined : stopTour}
    >
      {targetRect ? (
        <div
          className="page-tour-highlight"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      ) : null}
      <div
        className="page-tour-card"
        style={{ top: cardTop, left: cardLeft, width: cardWidth }}
        onClick={(event) => event.stopPropagation()}
        ref={(node) => {
          if (node) {
            const next = { width: node.offsetWidth, height: node.offsetHeight };
            if (next.width !== cardSize.width || next.height !== cardSize.height) {
              setCardSize(next);
            }
          }
        }}
      >
        <span className="page-tour-card-accent" aria-hidden="true" />
        <div className="page-tour-card-header">
          <div className="page-tour-kicker">
            <span className="page-tour-badge">{categoryLabel}</span>
            <span className="page-tour-step">{stepLabel}</span>
          </div>
          <button
            type="button"
            className="page-tour-close"
            onClick={stopTour}
            aria-label={t("how_it_works_skip")}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <h2 id="page-tour-title" className="page-tour-title">
          {detailsOpen ? title : cardTitle}
        </h2>
        <p className="page-tour-body">{detailsOpen ? body : cardBody}</p>
        {!detailsOpen && hasDetails ? (
          <button type="button" className="page-tour-more" onClick={openDetails}>
            {t("how_it_works_more_details")}
            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </button>
        ) : null}
        <div className="page-tour-keys" aria-hidden="true">
          <span className="page-tour-key-group">
            <span className="page-tour-key">←</span>
            <span className="page-tour-key">→</span>
            <span>{t("how_it_works_to_navigate", { defaultValue: "To navigate" })}</span>
          </span>
          <span className="page-tour-keys-sep" />
          <span className="page-tour-key-group">
            <span className="page-tour-key">Esc</span>
            <span>{t("how_it_works_to_exit", { defaultValue: "To exit" })}</span>
          </span>
        </div>
        <div className="page-tour-actions">
          <button type="button" className="page-tour-skip" onClick={stopTour}>
            {t("how_it_works_skip")}
          </button>
          <div className="page-tour-nav">
            <button
              type="button"
              className="page-tour-back"
              onClick={detailsOpen ? prevDetail : prevPage}
              disabled={!detailsOpen && isFirstPage}
            >
              {t("how_it_works_back")}
            </button>
            <button
              type="button"
              className="page-tour-next"
              onClick={detailsOpen ? nextDetail : nextPage}
            >
              {nextLabel}
              {!(detailsOpen ? isLastDetail : isLastPage) ? (
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
